import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import { fetchCachedJobs } from './usePipelineJobs';

export interface FilterOptions {
  jobs: Array<{ id: string; brand_name: string | null; created_at: string }>;
  engines: string[];
  prompts: Array<{ prompt_id: string; text: string }>;
  stages: string[];
  jobIds: string[];
}

export type PipelineFilterSection = 'engines' | 'prompts' | 'stages' | 'jobs';
interface UsePipelineFilterOptionsOptions {
  enabled?: boolean;
  sections?: PipelineFilterSection[];
}

const ALL_SECTIONS: PipelineFilterSection[] = ['engines', 'jobs', 'prompts', 'stages'];
const CACHE_DURATION_MS = 60 * 60 * 1000;
const filterOptionsCache = new Map<
  string,
  {
    data: FilterOptions;
    timestamp: number;
    projectId: string;
  }
>();
const filterOptionsRequests = new Map<string, Promise<FilterOptions>>();
const filterOptionsVersions = new Map<string, number>();
const EMPTY_FILTER_OPTIONS: FilterOptions = {
  jobs: [],
  engines: [],
  prompts: [],
  stages: [],
  jobIds: [],
};

/** Fetch only the requested filters. Omitted sections preserve the full legacy inventory. */
export function usePipelineFilterOptions(options: UsePipelineFilterOptionsOptions = {}) {
  const { enabled = true, sections } = options;
  const { user } = useAuth();
  const { currentProject, loading: projectLoading, isReady } = useProject();
  const projectId = currentProject?.id || null;
  const sectionsKey = [...new Set(sections ?? ALL_SECTIONS)].sort().join(',');
  const explicitSections = sections !== undefined;
  const inventoryRevision =
    currentProject?.topic_options_revision || currentProject?.prompt_topics_revision || '';
  const cacheKey =
    user && projectId
      ? JSON.stringify([user.uid, projectId, inventoryRevision, sectionsKey])
      : null;
  const activeRequestVersionRef = useRef(0);
  const dataCacheKeyRef = useRef<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(EMPTY_FILTER_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCacheKey, setErrorCacheKey] = useState<string | null>(null);

  const fetchFilterOptions = useCallback(
    async (force = false): Promise<FilterOptions> => {
      if (!user || !projectId || !cacheKey) return EMPTY_FILTER_OPTIONS;
      const cached = filterOptionsCache.get(cacheKey);
      if (!force && cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) return cached.data;
      const requestKey = `${cacheKey}:${force ? 'force' : 'cached'}`;
      const pending = filterOptionsRequests.get(requestKey);
      if (pending) return pending;
      const cacheVersion = (filterOptionsVersions.get(cacheKey) || 0) + 1;
      filterOptionsVersions.set(cacheKey, cacheVersion);
      const request = (async () => {
        const token = await user.getIdToken();
        const requestedSections = sectionsKey
          ? (sectionsKey.split(',') as PipelineFilterSection[])
          : [];
        const includeJobs = requestedSections.includes('jobs');
        const [jobs, res] = await Promise.all([
          includeJobs ? fetchCachedJobs(token, projectId, force) : Promise.resolve([]),
          fetch('/api/pipeline-ui', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              query_type: 'filter_options',
              params: {
                project_id: projectId,
                ...(explicitSections ? { filter_sections: requestedSections } : {}),
              },
            }),
          }),
        ]);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to fetch filter options');
        }
        const result = await res.json();
        const data: FilterOptions = {
          jobs: jobs.map(job => ({
            id: job.id,
            brand_name: job.brand_name,
            created_at: job.created_at,
          })),
          engines: result.data?.engines || [],
          prompts: result.data?.prompts || [],
          stages: result.data?.stages || [],
          jobIds: jobs.map(job => job.id),
        };
        if (filterOptionsVersions.get(cacheKey) === cacheVersion) {
          filterOptionsCache.set(cacheKey, { data, timestamp: Date.now(), projectId });
        }
        return data;
      })();
      filterOptionsRequests.set(requestKey, request);
      try {
        return await request;
      } finally {
        if (filterOptionsRequests.get(requestKey) === request)
          filterOptionsRequests.delete(requestKey);
      }
    },
    [user, projectId, cacheKey, sectionsKey, explicitSections]
  );

  useEffect(() => {
    const requestVersion = ++activeRequestVersionRef.current;
    let cancelled = false;
    const isLatestRequest = () => !cancelled && activeRequestVersionRef.current === requestVersion;
    const cleanup = () => {
      cancelled = true;
      activeRequestVersionRef.current += 1;
    };
    if (!enabled || !user || projectLoading || !isReady || !cacheKey) {
      setLoading(false);
      setError(null);
      setErrorCacheKey(null);
      return cleanup;
    }
    const cached = filterOptionsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      setFilterOptions(cached.data);
      dataCacheKeyRef.current = cacheKey;
      setLoading(false);
      setError(null);
      setErrorCacheKey(null);
      return cleanup;
    }
    setLoading(true);
    setError(null);
    setErrorCacheKey(null);
    fetchFilterOptions()
      .then(data => {
        if (!isLatestRequest()) return;
        setFilterOptions(data);
        dataCacheKeyRef.current = cacheKey;
      })
      .catch(err => {
        if (!isLatestRequest()) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch filter options');
        setErrorCacheKey(cacheKey);
      })
      .finally(() => {
        if (isLatestRequest()) setLoading(false);
      });
    return cleanup;
  }, [enabled, user, projectId, projectLoading, isReady, cacheKey, fetchFilterOptions]);

  const refresh = useCallback(async () => {
    if (!enabled || !user || projectLoading || !isReady || !cacheKey) return;
    const requestVersion = ++activeRequestVersionRef.current;
    const isLatestRequest = () => activeRequestVersionRef.current === requestVersion;
    setLoading(true);
    setError(null);
    setErrorCacheKey(null);
    try {
      const data = await fetchFilterOptions(true);
      if (!isLatestRequest()) return;
      setFilterOptions(data);
      dataCacheKeyRef.current = cacheKey;
    } catch (err) {
      if (!isLatestRequest()) return;
      setError(err instanceof Error ? err.message : 'Failed to refresh');
      setErrorCacheKey(cacheKey);
    } finally {
      if (isLatestRequest()) setLoading(false);
    }
  }, [enabled, user, projectLoading, isReady, cacheKey, fetchFilterOptions]);

  // Hide previous project/user/section data immediately, before the next effect runs.
  const hasCurrentData = Boolean(cacheKey && dataCacheKeyRef.current === cacheKey);
  const currentOptions = hasCurrentData ? filterOptions : EMPTY_FILTER_OPTIONS;
  const currentError = errorCacheKey === cacheKey ? error : null;
  const canLoad = Boolean(enabled && user && !projectLoading && isReady && cacheKey);
  return {
    filterOptions: currentOptions,
    ...currentOptions,
    loading:
      enabled && (projectLoading || (canLoad && (loading || (!hasCurrentData && !currentError)))),
    error: currentError,
    refresh,
    projectId,
    dataProjectId: hasCurrentData ? projectId : null,
    promptsReady:
      canLoad &&
      hasCurrentData &&
      !loading &&
      !currentError &&
      sectionsKey.split(',').includes('prompts'),
  };
}

export function clearPipelineFilterOptionsCache(projectId?: string) {
  for (const [key, cached] of filterOptionsCache) {
    if (!projectId || cached.projectId === projectId) filterOptionsCache.delete(key);
  }
  // Invalidate results still in flight as well, without disrupting other subscribers.
  for (const key of filterOptionsVersions.keys()) {
    const [, targetProjectId] = JSON.parse(key) as string[];
    if (!projectId || targetProjectId === projectId) {
      filterOptionsVersions.set(key, (filterOptionsVersions.get(key) || 0) + 1);
    }
  }
}

export { clearPipelineJobsCache } from './usePipelineJobs';
